/**
 * Paywall gate components
 *
 * <PaywallGate>          — hides children entirely when payments are off.
 * <AIFeatureGate>        — resolves availability across both config paths.
 *
 * Both render `null` while loading to avoid a flash of paid surface.
 *
 * `PaywallGate` and `NativePaymentGate` guard **money** surfaces and are
 * deliberately not BYOK-aware: a user on their own key is not buying anything,
 * and a deployment with payments off has nothing to sell.
 */

import type { ReactNode } from "react";
import {
	useAIFeatureEnabled,
	useNativePaymentsEnabled,
	usePaywall,
} from "@shared/hooks/use-paywall";
import { ByokRequiredCta } from "./ByokRequiredCta";

export interface PaywallGateProps {
	children: ReactNode;
	fallback?: ReactNode;
}

export function PaywallGate({ children, fallback = null }: PaywallGateProps) {
	const { enabled, isLoading } = usePaywall();
	if (isLoading) return null;
	if (!enabled) return <>{fallback}</>;
	return <>{children}</>;
}

export interface AIFeatureGateProps {
	featureKey: string;
	children: ReactNode;
	/**
	 * Rendered instead of the children when the feature is unavailable.
	 *
	 * Omitting it is not the same as passing `null`: an omitted fallback lets
	 * the gate substitute the BYOK unlock CTA when a key is all that is
	 * missing, whereas an explicit `null` means the caller has decided this
	 * surface stays silent.
	 */
	fallback?: ReactNode;
	/**
	 * Rendered while availability is still resolving. Defaults to `null` — a
	 * flash of the unlock CTA in front of a user who already has a working key
	 * is its own bug — but a page that owns its layout can pass a skeleton so
	 * the space is never empty.
	 */
	loadingFallback?: ReactNode;
}

/**
 * Three outcomes, not two:
 *
 * - available → children
 * - a key would unlock it → the inline CTA (unless the caller supplied a
 *   fallback of their own, which always wins)
 * - the feature does not exist here → nothing, exactly as before
 */
export function AIFeatureGate({
	featureKey,
	children,
	fallback,
	loadingFallback = null,
}: AIFeatureGateProps) {
	const { enabled, isLoading, reason } = useAIFeatureEnabled(featureKey);
	if (isLoading) return <>{loadingFallback}</>;
	if (enabled) return <>{children}</>;
	if (fallback !== undefined) return <>{fallback}</>;
	if (reason === "byok_key_required") {
		return <ByokRequiredCta featureKey={featureKey} />;
	}
	return null;
}

export interface NativePaymentGateProps {
	children: ReactNode;
	fallback?: ReactNode;
}

/**
 * Hides *purchase* call-to-action surfaces ("Top up", "Buy", "Upgrade")
 * on platforms where in-app purchasing is not yet available.
 *
 * On iOS this stays closed until the StoreKit IAP product is approved
 * (`PaymentConfig.payments_enabled_native_ios`). Required to avoid an Apple
 * Guideline 3.1.1 rejection — the binary must not expose any way to pay for
 * digital goods outside of approved in-app purchases.
 *
 * Unlike `<PaywallProtectedRoute>` this does NOT redirect; it simply omits
 * the CTA so the surrounding page (e.g. the wallet balance) still renders.
 */
export function NativePaymentGate({
	children,
	fallback = null,
}: NativePaymentGateProps) {
	const { enabled, isLoading } = useNativePaymentsEnabled();
	if (isLoading) return null;
	if (!enabled) return <>{fallback}</>;
	return <>{children}</>;
}
